import { useCallback, useMemo, type ReactElement } from 'react'
import { confirmDelete, useRequiredParams } from '@util'
import { DashboardDataSchema, type DashboardData, type DashboardId } from '@terros/common'
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { showNotification } from '@mantine/notifications'
import { useForm } from '@mantine/form'
import { useToastError } from '@hooks'
import {
  useDashboard,
  useRemoveDashboard,
  useReportColumnsMetadata,
  useTranslation,
  useUpdateDashboard,
  zodResolver,
} from '@front/model'
import { FormBlocker, NotReady } from '@components'
import { formatDashboardValidationError } from './dashboardValidation'
import { DashboardDraftProvider, type DashboardDraftContextValue } from './dashboardDraftContext'

/**
 * Layout for the dashboard build screen and its widget configuration modal. Owns
 * the draft so widget edits land in the same dashboard the build screen writes;
 * only this route talks to the server.
 */
function DashboardEditLayout(): ReactElement {
  const { dashboardId } = useRequiredParams<{ dashboardId: DashboardId }>()
  const { t } = useTranslation()
  const { data: dashboard, isLoading, error } = useDashboard(dashboardId)
  const {
    columns: reportColumns,
    columnsMap: reportColumnsMap,
    variantMap: reportColumnVariantMap,
  } = useReportColumnsMetadata()

  if (isLoading || error || !dashboard)
    return <NotReady data={dashboard} error={error} id='Dashboard' isLoading={isLoading} />

  // There is no read-only view: the build screen is the only way to open a
  // dashboard, so it double-checks the API's editable flag for direct links.
  if (dashboard.editable !== true) return <NotReady error={t('You do not have permission to edit this dashboard')} />

  return (
    <DashboardDraft
      dashboard={dashboard}
      reportColumnVariantMap={reportColumnVariantMap}
      reportColumns={reportColumns}
      reportColumnsMap={reportColumnsMap}
    />
  )
}

type DraftProps = {
  dashboard: DashboardDraftContextValue['dashboard']
  reportColumnVariantMap: DashboardDraftContextValue['reportColumnVariantMap']
  reportColumns: DashboardDraftContextValue['reportColumns']
  reportColumnsMap: DashboardDraftContextValue['reportColumnsMap']
}

function DashboardDraft({ dashboard, reportColumnVariantMap, reportColumns, reportColumnsMap }: DraftProps): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const form = useForm<DashboardData>({
    initialValues: dashboard,
    validate: zodResolver(DashboardDataSchema),
  })
  const { update, isUpdating, error: updateError } = useUpdateDashboard()
  const { remove, isRemoving, error: removeError } = useRemoveDashboard()
  useToastError(updateError)
  useToastError(removeError)

  const save = useCallback(async (): Promise<void> => {
    const validation = form.validate()
    if (validation.hasErrors) {
      showNotification({
        title: t('Error'),
        message: formatDashboardValidationError(validation.errors, form.getValues()),
        color: 'red',
      })
      return
    }

    try {
      await update({ ...form.getValues(), name: form.values.name?.trim() })
    } catch {
      // The toast already fired; stay put so the edits aren't lost.
      return
    }
    form.resetDirty()
    await navigate({ to: '/dashboard' })
  }, [form, navigate, t, update])

  const cancel = useCallback((): void => {
    form.reset()
    navigate({ to: '/dashboard' })
  }, [form, navigate])

  const removeDashboard = useCallback(async (): Promise<void> => {
    const confirmed = await confirmDelete({
      title: t('Delete Dashboard'),
      message: t('"{{name}}" will be removed for everyone.', {
        name: dashboard.name?.trim() || t('This dashboard'),
      }),
      t,
    })
    if (!confirmed) return

    await remove({ dashboardId: dashboard.dashboardId })
    form.resetDirty()
    await navigate({ to: '/dashboard' })
  }, [dashboard.dashboardId, dashboard.name, form, navigate, remove, t])

  const value = useMemo(
    (): DashboardDraftContextValue => ({
      form,
      dashboard,
      reportColumnVariantMap,
      reportColumns,
      reportColumnsMap,
      isSaving: isUpdating,
      isRemoving,
      save,
      cancel,
      remove: removeDashboard,
    }),
    [cancel, dashboard, form, isRemoving, isUpdating, removeDashboard, reportColumnVariantMap, reportColumns, reportColumnsMap, save]
  )

  return (
    <DashboardDraftProvider value={value}>
      <FormBlocker form={form} />
      <Outlet />
    </DashboardDraftProvider>
  )
}

export const Route = createFileRoute('/dashboard_/$dashboardId_/edit')({
  component: DashboardEditLayout,
})
